import * as React from 'react';

import { useUndoCancellationDataForXdp } from 'bundles/subscriptions-common/utils/cancellationUndoUtils';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import AssignButtonNotConfigurable from 'bundles/unified-description-page-common/components/admin/AssignButtonNotConfigurable';
import ConsumerProjectEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/consumer/ConsumerProjectEnrollButtonNotConfigurable';
import HeroCourseraPlusOrLiteCta from 'bundles/unified-description-page-common/components/modules/hero/HeroCourseraPlusOrLiteCta';
import HeroInstructors from 'bundles/unified-description-page-common/components/modules/hero/HeroInstructors';
import HeroLanguageInfo from 'bundles/unified-description-page-common/components/modules/hero/HeroLanguageInfo';
import HeroModule from 'bundles/unified-description-page-common/components/modules/hero/HeroModule';
import HeroPartnerLogo from 'bundles/unified-description-page-common/components/modules/hero/HeroPartnerLogo';
import HeroTotalEnrollmentCount from 'bundles/unified-description-page-common/components/modules/hero/HeroTotalEnrollmentCount';
import KeyInformationHorizontalModule from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationHorizontalModule';
import LearningObjectives from 'bundles/unified-description-page-common/components/modules/product-overview/LearningObjectives';
import ProductOverviewHorizontalModule from 'bundles/unified-description-page-common/components/modules/product-overview/ProductOverviewHorizontalModule';
import Skills from 'bundles/unified-description-page-common/components/modules/product-overview/Skills';
import Details from 'bundles/unified-description-page-common/components/modules/product-overview/details/Details';
import CoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/shared/CoBrandingPartnerLogos';
import { HERO_BACKGROUND_COLOR } from 'bundles/unified-description-page-common/constants';
import { usePageInfo } from 'bundles/unified-description-page-common/contexts/PageInfoContext';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';
import { useCourseraPlusOwnership } from 'bundles/unified-description-page-common/contexts/consumer/CourseraPlusOwnershipContext';
import AboutSubmoduleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/AboutSubmoduleRenderer';
import { useXdpSubscriptionsForwardDiscovery } from 'bundles/unified-description-page-common/providers/SubscriptionsForwardDiscoveryDataProvider';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';
import type { ProductOverviewModuleConfig } from 'bundles/unified-description-page-common/types/moduleConfig';
import { getConsumerProjectKeyInformationItems } from 'bundles/unified-description-page-common/utils/getConsumerKeyInformationItems';
import getPartnersForCourse from 'bundles/unified-description-page-common/utils/getPartnersForCourse';
import getProductDetailItems from 'bundles/unified-description-page-common/utils/getProductDetailItems';
import getProjectPageTypeName from 'bundles/unified-description-page-common/utils/getProjectPageTypeName';
import {
  getDescriptionTitle,
  getSkillsTitle,
} from 'bundles/unified-description-page-common/utils/getSectionTitlesForProject';

type Props = {
  pageData: ConsumerCoursePageFieldsFragment;
  config?: ProductOverviewModuleConfig;
};

const ProductOverviewModuleRenderer = ({ pageData, config }: Props) => {
  const productTitle = getProductTitle(pageData.name);
  const partnersToDisplay = getPartnersForCourse(pageData.partners);
  const { navigationLinks } = usePageNavigation();
  const courseraPlusOwnership = useCourseraPlusOwnership();
  const { showPlusMonthlyToAnnualUpgradeButton, isCourseraPlusEligibleProduct } = courseraPlusOwnership ?? {};
  const { showUndoCancellationCTA } = useUndoCancellationDataForXdp({ isCourseraPlusEligibleProduct });
  const showEnrollmentCountOnReviewsItem = showPlusMonthlyToAnnualUpgradeButton || showUndoCancellationCTA;
  const { pageType, navigationIdMap } = usePageInfo();
  const pageTypeName = getProjectPageTypeName(pageType);
  const overviewSkills = (
    <Skills skills={pageData.skills} title={getSkillsTitle(pageType)} disableCTA={config?.removeLinksAboveFold} />
  );
  const overviewDescription = (
    <LearningObjectives learningObjectives={pageData.cmlLearningObjectives} title={getDescriptionTitle(pageType)} />
  );

  const details = <Details detailItems={getProductDetailItems(pageData, config?.productDetailItems || [], pageType)} />;
  const { shouldShowXdpChanges } = useXdpSubscriptionsForwardDiscovery();

  const { items: keyInformationItems } = getConsumerProjectKeyInformationItems(
    pageData,
    {
      ...config?.keyInfoModule,
    },
    config?.removeLinksAboveFold,
    pageType,
    showEnrollmentCountOnReviewsItem,
    shouldShowXdpChanges
  );

  const getAdditionalInfo = () => {
    if (showEnrollmentCountOnReviewsItem) {
      return null;
    }

    return <HeroTotalEnrollmentCount count={pageData.totalEnrollmentCount} />;
  };

  const getAdditionalCta = () => {
    if (showEnrollmentCountOnReviewsItem) {
      return null;
    }

    return (
      !config?.hideCourseraPlus &&
      courseraPlusOwnership?.isUserEligibleToSeeCta && (
        <HeroCourseraPlusOrLiteCta
          disableCTA={config?.removeLinksAboveFold}
          isSubscribedToCPlusOrLite={courseraPlusOwnership.isSubscribedToCPlusOrLite}
          isSubscribedToCourseraLite={courseraPlusOwnership.isSubscribedToCourseraLite}
        />
      )
    );
  };

  return (
    <ProductOverviewHorizontalModule
      anchorId={navigationIdMap?.ABOUT}
      heroBackgroundColor={HERO_BACKGROUND_COLOR}
      navigationLinks={navigationLinks}
      hero={
        <HeroModule
          productTitle={productTitle}
          backgroundImage={config?.backgroundImage}
          partnerLogo={
            partnersToDisplay && partnersToDisplay.length > 1 ? (
              <CoBrandingPartnerLogos partners={partnersToDisplay} isNavPlacement={false} />
            ) : (
              <HeroPartnerLogo image={partnersToDisplay?.[0]?.primaryLogo} partnerName={partnersToDisplay?.[0]?.name} />
            )
          }
          description={
            config?.showHeroLanguageInfo && (
              <HeroLanguageInfo
                languages={{
                  primaryLanguages: pageData.primaryLanguages,
                  subtitleLanguages: pageData.subtitleLanguages ?? [],
                  translatedLanguages: pageData.translatedLanguages ?? [],
                  dubbedLanguages: pageData.dubbedLanguages ?? [],
                }}
              />
            )
          }
          instructors={
            !config?.hideInstructors && (
              <HeroInstructors
                instructors={pageData.instructors}
                averageInstructorRating={pageData.averageInstructorRating}
                instructorRatingCount={pageData.instructorRatingCount}
                partners={pageData.partners}
              />
            )
          }
          primaryCta={
            <ConsumerProjectEnrollButtonNotConfigurable pageData={pageData} productDisplayName={pageTypeName} />
          }
          secondaryCta={
            config?.showAssignButton && (
              <AssignButtonNotConfigurable productSlug={pageData.slug} productType="project" />
            )
          }
          additionalInfo={getAdditionalInfo()}
          additionalCta={getAdditionalCta()}
        />
      }
      keyInformation={
        <KeyInformationHorizontalModule
          keyInformationItems={keyInformationItems}
          applyLightShadow={!!config?.heroModule?.backgroundColor}
        />
      }
      aboutSubmodule={
        <AboutSubmoduleRenderer
          skills={overviewSkills}
          description={overviewDescription}
          details={details}
          config={config}
        />
      }
    />
  );
};

export default ProductOverviewModuleRenderer;
